/**
 * Şablonla doldurma — "bir referans belge göster, gerisini aynı şekilde oku".
 *
 * NEDEN ÇIPA, NEDEN KOORDİNAT DEĞİL
 *
 * En kolay yol, kullanıcının işaretlediği değerin x/y konumunu kaydedip
 * sonraki belgede aynı noktaya bakmaktı. Ama faturalar kayar: bir kalem
 * eklenince alttaki her şey yukarı iter, farklı bir yazıcı kenar boşluğunu
 * değiştirir, logo bir satır büyür. Mutlak koordinat ilk farklı belgede
 * çöker — ve sessizce yanlış hücreyi okur.
 *
 * Bunun yerine ÇIPA kaydedilir: *"«Toplam» yazısının sağındaki ilk sayı"*.
 * Etiket belgeyle birlikte kayar, kural bozulmaz. Kayan bir belgede
 * koordinat yanlış cevap verir; çıpa ya doğru cevabı bulur ya hiç bulamaz.
 * İkincisi kabul edilebilir, birincisi değil.
 *
 * BULAMAMAK BAŞARISIZLIK DEĞİLDİR. Alan boş kalır ve işaretlenir; sistem
 * asla "buraya yakın bir şey vardı, o olsun" demez.
 */

import type { Locale } from './locale.js';
import { parseAmount } from './amount.js';
import { parseDate } from './date.js';
import { type Parsed, none } from './parsed.js';

/** pdf.js'in verdiği konumlu metin parçası. */
export interface TextItem {
  readonly str: string;
  /** Soldan uzaklık. */
  readonly x: number;
  /** Alttan yükseklik — PDF'te y YUKARI doğru artar. */
  readonly y: number;
}

export type FieldKind = 'amount' | 'date' | 'text';
export type Direction = 'right' | 'below';

export interface FieldAnchor {
  /** Bu alanın defterdeki adı: `amount`, `date`, `note`, `invoiceNo`… */
  readonly field: string;
  /** Belgede aranacak etiket metni. */
  readonly label: string;
  readonly direction: Direction;
  readonly kind: FieldKind;
}

export interface Template {
  readonly id: string;
  readonly name: string;
  readonly fields: readonly FieldAnchor[];
}

/** Aynı satır sayılmak için y farkı bu kadarı geçmemeli. */
const LINE_TOLERANCE = 4;
/** Alt alta sayılmak için x farkı bu kadarı geçmemeli. */
const COLUMN_TOLERANCE = 40;

function norm(s: string): string {
  return String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Etiket gibi mi görünüyor: içinde harf var ve tamamen sayı değil. */
function looksLikeLabel(s: string): boolean {
  const t = String(s ?? '').trim();
  if (!t) return false;
  if (!/\p{L}/u.test(t)) return false;
  return t.length <= 40;
}

/* ── Öğrenme ───────────────────────────────────────────────────────── */

/**
 * Kullanıcının işaretlediği değer için bir çıpa öğren.
 *
 * Önce AYNI SATIRDA solundaki en yakın etiket aranır ("Toplam: 1.234"),
 * bulunamazsa ÜSTÜNDEKİ en yakın etikete bakılır (sütun başlığı).
 * İkisi de yoksa null — uydurma çıpa üretilmez.
 *
 * @param items   Belgedeki tüm konumlu metin parçaları.
 * @param target  Kullanıcının işaretlediği parçanın dizini.
 */
export function learnAnchor(
  items: readonly TextItem[],
  target: number,
  field: string,
  kind: FieldKind,
): FieldAnchor | null {
  const v = items[target];
  if (!v) return null;

  /* 1. Aynı satırda solda. */
  let best: TextItem | null = null;
  for (const it of items) {
    if (it === v) continue;
    if (Math.abs(it.y - v.y) > LINE_TOLERANCE) continue;
    if (it.x >= v.x) continue;
    if (!looksLikeLabel(it.str)) continue;
    if (!best || it.x > best.x) best = it;
  }
  if (best) {
    return { field, label: best.str.trim(), direction: 'right', kind };
  }

  /* 2. Üstünde, aynı sütunda. PDF'te y yukarı arttığı için "üst" = büyük y. */
  for (const it of items) {
    if (it === v) continue;
    if (it.y <= v.y) continue;
    if (Math.abs(it.x - v.x) > COLUMN_TOLERANCE) continue;
    if (!looksLikeLabel(it.str)) continue;
    if (!best || it.y < best.y) best = it;
  }
  if (best) {
    return { field, label: best.str.trim(), direction: 'below', kind };
  }

  return null;
}

/* ── Uygulama ──────────────────────────────────────────────────────── */

/** Etiketi belgede bul. Birden çok eşleşirse ilki (en üstteki) kullanılır. */
function findLabel(items: readonly TextItem[], label: string): TextItem | null {
  const want = norm(label);
  if (!want) return null;
  let hit: TextItem | null = null;
  for (const it of items) {
    const n = norm(it.str);
    /* Tam eşleşme tercih edilir; yoksa etiketi İÇEREN parça kabul edilir
       ("Toplam" ile "Genel Toplam:" aynı hücrede olabiliyor). */
    if (n === want) return it;
    if (!hit && n.includes(want)) hit = it;
  }
  return hit;
}

/** Çıpanın işaret ettiği değer parçasını bul. */
function valueFor(items: readonly TextItem[], anchor: FieldAnchor): TextItem | null {
  const lbl = findLabel(items, anchor.label);
  if (!lbl) return null;

  if (anchor.direction === 'right') {
    let best: TextItem | null = null;
    for (const it of items) {
      if (it === lbl) continue;
      if (Math.abs(it.y - lbl.y) > LINE_TOLERANCE) continue;
      if (it.x <= lbl.x) continue;
      if (!String(it.str ?? '').trim()) continue;
      if (!best || it.x < best.x) best = it;
    }
    return best;
  }

  let best: TextItem | null = null;
  for (const it of items) {
    if (it === lbl) continue;
    if (it.y >= lbl.y) continue;                       // altında
    if (Math.abs(it.x - lbl.x) > COLUMN_TOLERANCE) continue;
    if (!String(it.str ?? '').trim()) continue;
    if (!best || it.y > best.y) best = it;             // en yakın alt
  }
  return best;
}

export interface FieldResult {
  readonly field: string;
  /** Ham metin. Bulunamadıysa boş dize. */
  readonly raw: string;
  /** Türüne göre çözümlenmiş değer. */
  readonly parsed: Parsed<string | number>;
}

/**
 * Şablonu bir belgeye uygula.
 *
 * Bulunamayan alan `value: null` ile döner ve arayüzde soru işaretiyle
 * gösterilir. Yakınındaki başka bir değeri "herhalde budur" diye almak,
 * sessizce yanlış veri yazmanın en kolay yoludur; yapılmaz.
 */
export function applyTemplate(
  items: readonly TextItem[],
  template: Template,
  locale: Locale,
): FieldResult[] {
  return template.fields.map((anchor) => {
    const hit = valueFor(items, anchor);
    if (!hit) {
      return {
        field: anchor.field,
        raw: '',
        parsed: none<string | number>(
          `"${anchor.label}" was not found in this document - fill the field in manually.`,
        ),
      };
    }
    const raw = String(hit.str ?? '').trim();
    if (anchor.kind === 'amount') {
      return { field: anchor.field, raw, parsed: parseAmount(raw, locale) };
    }
    if (anchor.kind === 'date') {
      return { field: anchor.field, raw, parsed: parseDate(raw, locale) };
    }
    return { field: anchor.field, raw, parsed: { value: raw, certain: true } };
  });
}

/**
 * Şablonun bu belgeye ne kadar uyduğu — 0 ile 1 arası.
 *
 * Kullanıcıya doğru şablonu seçmekte yardımcı olur ve yanlış şablonun
 * sessizce uygulanmasını engeller: eşleşme düşükse arayüz uyarır.
 */
export function templateFit(items: readonly TextItem[], template: Template): number {
  if (!template.fields.length) return 0;
  const found = template.fields.filter((f) => findLabel(items, f.label) !== null).length;
  return found / template.fields.length;
}
